import { WModalButton, type WModalButtonProps } from "@/components/button/WToolButton.tsx";
import { WModal, WModalContent } from "@/components/modal/WModal.tsx";
import { useStartImpersonation } from "@/modules/access/impersonation/accessImpersonationApi.ts";
import { Trans, useLingui } from "@lingui/react/macro";
import type { AccessImpersonation } from "@warrenio/api-spec/spec.oats.gen";

export interface ActAsModalProps {
    item: AccessImpersonation;
}

export function ActAsModalContent({ item }: ActAsModalProps) {
    const startImpersonation = useStartImpersonation();

    return (
        <ActAsModalCommon
            name={item.access_owner}
            action={async () => {
                await startImpersonation.mutateAsync(item);
            }}
        />
    );
}

export function ActAsModalCommon({ name, action }: { name: string; action: () => Promise<void> }) {
    const { t } = useLingui();

    return (
        <WModalContent
            title={t({ comment: "Dialog title", message: "Access user" })}
            label={t({ comment: "Button action", message: "Start" })}
            autoFocus
            modalAction={action}
        >
            <p className="pb-4">
                <Trans>You are about to access user "{name}"</Trans>
            </p>
            <p className="pb-4">
                <Trans>
                    The entire user interface will switch and act as you were that user. Once you are finished you can
                    stop the session by clicking “Back to your account” button on the header bar and the user interface
                    will switch back to your account.
                </Trans>
            </p>
            <p>
                <Trans>Keep in mind that all the actions will be tracked and can be traced back to you.</Trans>
            </p>
        </WModalContent>
    );
}

export function ActAsModal({
    buttonProps,
    ...props
}: { buttonProps?: Omit<WModalButtonProps, "label" | "icon"> } & ActAsModalProps) {
    const { t } = useLingui();
    return (
        <WModal button={<WModalButton inTable showLabel label={t`Act as`} icon="i-lucide:log-in" {...buttonProps} />}>
            <ActAsModalContent {...props} />
        </WModal>
    );
}
